import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { AdminCommandSnapshot, BotApi, RegionSeedResult, RegionSummary, SlotDesiredStateUpdate } from './BotApi';
import type {
  AgentHost,
  AdminOverview,
  AuditEventsPage,
  AuditEvent,
  ActionKey,
  BotCommand,
  BotSlot,
  BotStatus,
  CommandRequest,
  DashboardSettings,
  DesiredBotState,
  IntelligenceMapTarget,
  IntelligenceRequest,
  LicensePlan,
  LinkingEventType,
  LinkingSession,
  LinkingChatMessage,
  ReconnectSettings,
  SaveSlotRequest,
  UserProfile,
  WaitlistEntry
} from '../domain/types';
import { runtimeConfig } from '../config/runtimeConfig';

const config = runtimeConfig();
const supabaseUrl = config.supabaseUrl;
const supabaseAnonKey = config.supabaseAnonKey;
const AUTH_TIMEOUT_MS = 30000;
const PROFILE_TIMEOUT_MS = 20000;
const SLOT_SELECT =
  'id,slot_key,label,agent_id,license_status,linked,state,enabled_modules,region_id,region_status,region_verified_at,region_source,detected_region_id,detected_region_at,region_notes';
export const BOT_SLOT_DESIRED_STATE_SELECT =
  'desired_worker_state,desired_bot_state,desired_reason,desired_updated_at';
export const BOT_SLOT_OWNER_PROFILE_SELECT = 'profiles!bot_slots_user_id_fkey(email)';

let client: SupabaseClient | null = null;

async function withTimeout<T>(promise: PromiseLike<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(`${label} timeout`)), timeoutMs);
  });
  try {
    return await Promise.race([Promise.resolve(promise), timeout]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

function getClient(): SupabaseClient {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
  }
  client ||= createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      flowType: 'implicit',
      detectSessionInUrl: true,
      persistSession: true,
      autoRefreshToken: true
    }
  });
  return client;
}

async function wait(ms: number): Promise<void> {
  await new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function retry<T>(operation: () => Promise<T>, attempts: number, delayMs: number): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt < attempts) await wait(delayMs);
    }
  }
  throw lastError;
}

function mapSlot(row: any): BotSlot {
  const licenseRow = Array.isArray(row.licenses) ? row.licenses[0] : row.licenses;
  return {
    id: String(row.id),
    slotKey: String(row.slot_key),
    label: String(row.label || row.slot_key),
    userEmail: row.user_email || row.profiles?.email,
    agentId: String(row.agent_id || 'local-dev'),
    licenseStatus: row.license_status || 'inactive',
    planKey: licenseRow?.plan_key || null,
    licenseProvider: licenseRow?.provider || null,
    licensePlan: licensePlanFromRow(row, licenseRow),
    trialEndsAt: licenseRow?.trial_ends_at || null,
    linked: Boolean(row.linked),
    state: row.state || 'empty',
    enabledModules: {
      collect: row.enabled_modules?.collect !== false,
      train_troops: row.enabled_modules?.train_troops !== false,
      auto_hunt: row.enabled_modules?.auto_hunt !== false,
      intelligence: row.enabled_modules?.intelligence === true
    },
    regionId: row.region_id || null,
    regionStatus: row.region_status || 'unknown',
    regionSource: row.region_source || 'unknown',
    regionVerifiedAt: row.region_verified_at || null,
    detectedRegionId: row.detected_region_id || null,
    detectedRegionAt: row.detected_region_at || null,
    regionNotes: row.region_notes || null,
    desiredWorkerState: row.desired_worker_state || 'running',
    desiredBotState: row.desired_bot_state || 'idle',
    desiredReason: row.desired_reason || 'schema_default',
    desiredUpdatedAt: row.desired_updated_at || null
  };
}

function mapCommand(row: any): BotCommand {
  return {
    commandId: String(row.command_id),
    action: row.action,
    status: row.status,
    queuedAt: row.queued_at,
    completedAt: row.completed_at,
    error: row.error
  };
}

function fallbackQueuedCommand(commandId: string, action: ActionKey): BotCommand {
  return {
    commandId,
    action,
    status: 'queued',
    queuedAt: new Date().toISOString(),
    completedAt: null,
    error: null
  };
}

export function desiredBotStateForManualCommand(action: ActionKey): DesiredBotState {
  if (action === 'stop') return 'stopped';
  if (action === 'reconnect') return 'idle';
  return action;
}

function relatedMapTarget(target: any): any {
  if (Array.isArray(target)) return target[0] || null;
  return target || null;
}

function mapIntelligenceTarget(row: any): IntelligenceMapTarget | null {
  const target = relatedMapTarget(row);
  if (!target) return null;
  return {
    playerName: target.player_name || null,
    allianceTag: target.alliance_tag || null,
    allianceName: target.alliance_name || null,
    power: target.power == null ? null : Number(target.power),
    castleX: target.castle_x == null ? null : Number(target.castle_x),
    castleY: target.castle_y == null ? null : Number(target.castle_y),
    lastSeenAt: target.last_seen_at || null,
    confidence: target.confidence == null ? null : Number(target.confidence)
  };
}

function mapIntelligenceRequest(row: any): IntelligenceRequest {
  const rawResults = Array.isArray(row.intelligence_request_results)
    ? row.intelligence_request_results
    : [];
  const results = rawResults
    .map((r: any) => mapIntelligenceTarget(r.map_targets))
    .filter(Boolean) as IntelligenceMapTarget[];
  results.sort((a, b) => {
    const parsedLeft = a.lastSeenAt ? Date.parse(a.lastSeenAt) : 0;
    const parsedRight = b.lastSeenAt ? Date.parse(b.lastSeenAt) : 0;
    const left = Number.isFinite(parsedLeft) ? parsedLeft : 0;
    const right = Number.isFinite(parsedRight) ? parsedRight : 0;
    return right - left;
  });
  return {
    id: String(row.id),
    playerName: String(row.player_name || row.query || ''),
    regionId: row.region_id || null,
    status: row.status || 'pending',
    error: row.error || null,
    createdAt: row.created_at || row.requested_at,
    updatedAt: row.updated_at || row.completed_at || null,
    hiddenAt: row.user_hidden_at || null,
    results
  };
}

function relatedProfileEmail(profile: { email?: string } | { email?: string }[] | null | undefined): string | null {
  const item = Array.isArray(profile) ? profile[0] : profile;
  return item?.email || null;
}

function mapLinkingSession(row: any): LinkingSession {
  return {
    id: String(row.id),
    slotId: String(row.slot_id),
    slotKey: String(row.slot_key),
    status: row.status,
    expiresAt: row.expires_at,
    assistanceRequestedAt: row.assistance_requested_at ?? null,
    operatorActive: Boolean(row.operator_active)
  };
}

function mapLinkingChatMessage(row: any): LinkingChatMessage {
  return {
    id: String(row.id),
    sessionId: String(row.session_id),
    senderRole: row.sender_role === 'operator' ? 'operator' : 'user',
    body: String(row.body ?? ''),
    createdAt: row.created_at
  };
}

function mapAgentHost(row: any): AgentHost {
  return {
    agentId: String(row.agent_id),
    label: String(row.label || row.agent_id),
    status: String(row.status || 'offline'),
    lastSeenAt: row.last_seen_at || null,
    inventoryUpdatedAt: row.inventory_updated_at || null,
    inventory: row.inventory || {}
  };
}

function isMissingOptionalSettingsTable(error: any): boolean {
  return error?.code === '42P01' || error?.code === '42501' || error?.code === '42703';
}

function isRpcSignatureMismatch(error: any): boolean {
  const message = `${error?.message || ''} ${error?.details || ''}`.toLowerCase();
  return error?.code === 'PGRST202' || error?.code === 'PGRST203' || message.includes('schema cache') || message.includes('function');
}

function isMissingDesiredStateColumn(error: any): boolean {
  const message = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`.toLowerCase();
  return error?.code === '42703' || message.includes('desired_worker_state') || message.includes('desired_bot_state');
}

function desiredStatePayload(update: SlotDesiredStateUpdate) {
  return {
    desired_worker_state: update.desiredWorkerState,
    desired_bot_state: update.desiredBotState,
    desired_reason: update.desiredReason
  };
}

async function updateSlotDesiredStateIfAvailable(
  supabase: SupabaseClient,
  slotId: string,
  update: SlotDesiredStateUpdate
): Promise<BotSlot | null> {
  const { data, error } = await supabase
    .from('bot_slots')
    .update(desiredStatePayload(update))
    .eq('id', slotId)
    .select(`${SLOT_SELECT},${BOT_SLOT_DESIRED_STATE_SELECT},licenses(trial_ends_at,status,plan_key,provider)`)
    .maybeSingle();
  if (!error) return data ? mapSlot(data) : null;
  if (isMissingDesiredStateColumn(error)) return null;
  throw error;
}

async function fetchAdminSlotRows(supabase: SupabaseClient): Promise<any[]> {
  const baseSelect =
    `${SLOT_SELECT},user_id,licenses(trial_ends_at,status,plan_key,provider)`;
  const withDesired = `${SLOT_SELECT},${BOT_SLOT_DESIRED_STATE_SELECT},user_id,licenses(trial_ends_at,status,plan_key,provider)`;
  const withDesiredResult = await supabase
    .from('bot_slots')
    .select(withDesired)
    .order('slot_key', { ascending: true });
  if (!withDesiredResult.error) return withDesiredResult.data || [];
  if (!isMissingDesiredStateColumn(withDesiredResult.error)) throw withDesiredResult.error;

  const fallbackResult = await supabase
    .from('bot_slots')
    .select(baseSelect)
    .order('slot_key', { ascending: true });
  if (fallbackResult.error) throw fallbackResult.error;
  return fallbackResult.data || [];
}

function mapSlotSettings(row: any): DashboardSettings {
  const autoBotSettings = row.auto_bot_settings || {};
  return {
    autoBotModules: autoBotSettings.modules || autoBotSettings || {},
    autoBotHelp: typeof autoBotSettings.autoHelp === 'boolean' ? autoBotSettings.autoHelp : autoBotSettings.auto_help !== false,
    autoBotHeal: typeof autoBotSettings.autoHeal === 'boolean' ? autoBotSettings.autoHeal : autoBotSettings.auto_heal === true,
    autoBotShield: typeof autoBotSettings.autoShield === 'boolean' ? autoBotSettings.autoShield : autoBotSettings.auto_shield === true,
    collect: row.collect_settings || {},
    training: row.training_settings || {},
    hunt: row.auto_hunt_settings || {}
  } as DashboardSettings;
}

function slotSettingsPayload(slotId: string, settings: DashboardSettings) {
  return {
    slot_id: slotId,
    auto_bot_settings: { modules: settings.autoBotModules, autoHelp: settings.autoBotHelp, autoHeal: settings.autoBotHeal, autoShield: settings.autoBotShield },
    collect_settings: settings.collect,
    training_settings: settings.training,
    auto_hunt_settings: settings.hunt
  };
}

function reconnectSettingsPayload(slotId: string, settings: ReconnectSettings) {
  return {
    slot_id: slotId,
    reconnect_settings: {
      auto_enabled: settings.autoEnabled,
      delay_seconds: settings.delaySeconds
    }
  };
}

function mapAuditEvent(row: any): AuditEvent {
  return {
    id: String(row.id),
    createdAt: row.created_at,
    eventType: String(row.event_type || 'unknown'),
    userId: row.user_id || null,
    slotId: row.slot_id || null,
    agentId: row.agent_id || null,
    payload: row.payload || {}
  };
}

function mapAdminCommandSnapshot(row: any): AdminCommandSnapshot {
  return {
    commandId: String(row.command_id),
    action: String(row.action || ''),
    status: row.status || 'queued',
    queuedAt: row.queued_at || null,
    deliveredAt: row.delivered_at || null,
    completedAt: row.completed_at || null,
    error: row.error || null,
    result: row.result || null
  };
}

function toLicenseTableStatus(status: string) {
  if (status === 'active' || status === 'trial' || status === 'expired' || status === 'suspended') {
    return status;
  }
  return 'cancelled';
}

function licenseStatusFromPlan(plan: LicensePlan): SaveSlotRequest['licenseStatus'] {
  return plan === 'trial' ? 'trial' : 'active';
}

function planKeyFromLicensePlan(plan: LicensePlan): string {
  return plan === 'pro_plus' ? 'pro_plus' : plan;
}

function licensePlanFromStatus(status: string): LicensePlan {
  return status === 'trial' ? 'trial' : 'pro';
}

function licensePlanFromRow(row: any, licenseRow: any): LicensePlan {
  const planKey = String(licenseRow?.plan_key || '').toLowerCase();
  if (String(row.license_status || '') === 'trial' || planKey === 'trial') return 'trial';
  return ['pro_plus', 'pro+', 'pro-plus', 'proplus'].includes(planKey) ? 'pro_plus' : 'pro';
}

function fiveDaysFromNowIso(): string {
  return new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString();
}

function isPastIso(value?: string | null): boolean {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getTime() <= Date.now();
}

function mapProvisioning(slot: any, agentHost?: any): BotStatus['provisioning'] {
  if (!agentHost) return { status: 'blocked', label: 'sin agente', detail: 'El host local no esta registrado' };
  const inventory = agentHost.inventory || {};
  const profiles = inventory.profiles || [];
  const slotKey = String(slot?.slot_key || '');
  const profile = profiles.find((item: any) => Array.isArray(item.assigned_slots) && item.assigned_slots.includes(slotKey));
  if (!inventory || !Object.keys(inventory).length) {
    return { status: 'blocked', label: 'sin inventario', detail: 'Esperando inventario BlueStacks del bridge' };
  }
  if (!profile) {
    return { status: 'warning', label: 'sin perfil', detail: 'El slot no aparece en el inventario local' };
  }
  if (profile.health === 'ready') {
    return { status: 'ready', label: 'listo', detail: profile.profile };
  }
  if (profile.health === 'error') {
    return {
      status: 'blocked',
      label: 'perfil bloqueado',
      detail: [profile.profile, ...(profile.warnings || [])].filter(Boolean).join(' / ')
    };
  }
  return {
    status: 'warning',
    label: 'requiere revision',
    detail: [profile.profile, ...(profile.warnings || [])].filter(Boolean).join(' / ')
  };
}

function mapStatus(row: any, slotId: string, agentHost?: any, heartbeat?: any, slot?: any): BotStatus | null {
  if (!row) return null;
  const localStatus = heartbeat?.local_status || null;
  const screenshotPath = typeof row.screenshot_path === 'string' ? row.screenshot_path : null;
  const reconnect = row.reconnect_state || localStatus?.reconnect || null;
  const reconnectSettings = row.reconnect_settings || localStatus?.reconnect_settings || null;
  const latestLogLine = row.latest_log_line || localStatus?.latest_log_line || null;
  const localRuntimeStartedAt =
    typeof localStatus?.session_started_at === 'number'
      ? new Date(localStatus.session_started_at * 1000).toISOString()
      : localStatus?.session_started_at || null;
  return {
    slotId,
    state: row.state || 'idle',
    running: Boolean(row.running),
    bridgeStatus: agentHost?.status || heartbeat?.bridge_state || null,
    agentLastSeenAt: agentHost?.last_seen_at || heartbeat?.received_at || row.updated_at || null,
    localAvailable: localStatus ? localStatus.agent_state !== 'local_unavailable' : null,
    provisioning: mapProvisioning(slot, agentHost),
    lastResult: row.last_result,
    nextCheckSeconds: row.next_check_seconds,
    latestLogLine,
    recentLogs: Array.isArray(row.recent_logs) ? row.recent_logs : latestLogLine ? [latestLogLine] : [],
    runtimeSeconds:
      typeof row.runtime_seconds === 'number'
        ? row.runtime_seconds
        : typeof localStatus?.session_uptime_seconds === 'number'
          ? localStatus.session_uptime_seconds
          : null,
    runtimeStartedAt: row.runtime_started_at || localRuntimeStartedAt,
    nextCheckStartedAt: row.next_check_started_at || localStatus?.next_check_started_at || null,
    updatedAt: row.updated_at || null,
    screenshotUrl:
      screenshotPath && /^(https?:|data:image\/)/i.test(screenshotPath) ? screenshotPath : null,
    linkingAssistant: localStatus?.linking_assistant || row.linking_assistant || null,
    screenBlocker: row.screen_blocker || localStatus?.screen_blocker || null,
    reconnect: reconnect
      ? {
          scheduled: Boolean(reconnect.scheduled),
          running: Boolean(reconnect.running),
          deadline: reconnect.deadline ?? null,
          remainingSeconds: reconnect.remaining_seconds ?? reconnect.remainingSeconds ?? null,
          phase: reconnect.phase ?? null,
          result: reconnect.result ?? null
        }
      : null,
    reconnectSettings: reconnectSettings
      ? {
          autoEnabled: Boolean(reconnectSettings.auto_enabled ?? reconnectSettings.autoEnabled),
          delaySeconds: Number(reconnectSettings.delay_seconds ?? reconnectSettings.delaySeconds ?? 300)
        }
      : null,
    colonyQueues: (row.training_queue_status || []).map((item: any) => ({
      queue: item.queue,
      action: item.status || 'unknown',
      remaining: item.remaining
    })),
    worldQueues: (row.world_march_status || []).map((item: any) => ({
      queue: item.queue,
      action: item.status || 'unknown',
      remaining: item.remaining
    }))
  };
}

export const supabaseBotApi: BotApi = {
  mode: 'supabase',
  allowCommands: config.allowCommands,
  allowedCommandSlotKeys: config.allowedCommandSlotKeys,
  async getSession(): Promise<UserProfile | null> {
    const supabase = getClient();
    const { data } = await withTimeout(supabase.auth.getSession(), AUTH_TIMEOUT_MS, 'Supabase session');
    if (!data.session?.user) return null;
    const profileResult = await retry(
      () =>
        withTimeout(
          supabase
            .from('profiles')
            .select('id,email,role')
            .eq('id', data.session.user.id)
            .maybeSingle(),
          PROFILE_TIMEOUT_MS,
          'Supabase profile'
        ),
      4,
      750
    );
    if (profileResult.error || !profileResult.data) return null;
    return { id: profileResult.data.id, email: profileResult.data.email, role: profileResult.data.role };
  },
  async signInWithGoogle(): Promise<void> {
    const supabase = getClient();
    const redirectTo = `${window.location.origin}/dashboard`;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        skipBrowserRedirect: true
      }
    });
    if (error) throw error;
    if (!data.url) throw new Error('Google login URL was not returned');
    window.location.href = data.url;
  },
  async signInWithEmail(email: string): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.href }
    });
    if (error) throw error;
  },
  async signOut(): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },
  async getCurrentUser(): Promise<UserProfile> {
    const supabase = getClient();
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw error || new Error('Not authenticated');
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id,email,role')
      .eq('id', data.user.id)
      .single();
    if (profileError) throw profileError;
    return { id: profile.id, email: profile.email, role: profile.role };
  },
  async listSlots(options?: { scope?: 'own' | 'all' }): Promise<BotSlot[]> {
    const supabase = getClient();
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) throw userError || new Error('Not authenticated');
    let query = supabase
      .from('bot_slots')
      .select(`${SLOT_SELECT},${BOT_SLOT_OWNER_PROFILE_SELECT},licenses(trial_ends_at,status,plan_key,provider)`)
      .order('created_at', { ascending: true });
    if (options?.scope !== 'all') {
      query = query.eq('user_id', userData.user.id);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(mapSlot);
  },
  async claimNextAvailableSlot(): Promise<BotSlot | null> {
    const supabase = getClient();
    const { data, error } = await supabase.rpc('claim_next_available_slot');
    if (!error) return data ? mapSlot(data) : null;
    if (isRpcSignatureMismatch(error)) return null;
    throw error;
  },
  async getSlotStatus(slotId: string): Promise<BotStatus | null> {
    const supabase = getClient();
    const [slotResult, statusResult] = await Promise.all([
      supabase.from('bot_slots').select('id,slot_key,agent_id').eq('id', slotId).maybeSingle(),
      supabase.from('slot_status').select('*').eq('slot_id', slotId).maybeSingle()
    ]);
    if (slotResult.error) throw slotResult.error;
    const { data, error } = statusResult;
    if (error) throw error;
    if (!data) return null;

    const agentId = data.agent_id || slotResult.data?.agent_id;
    const [hostResult, heartbeatResult] = await Promise.all([
      agentId
        ? supabase
            .from('agent_hosts')
            .select('status,last_seen_at,inventory,inventory_updated_at')
            .eq('agent_id', agentId)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      agentId
        ? supabase
            .from('agent_heartbeats')
            .select('bridge_state,local_status,received_at')
            .eq('agent_id', agentId)
            .order('received_at', { ascending: false })
            .limit(1)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null })
    ]);
    // agent_hosts + agent_heartbeats are optional enrichment for bridge status.
    // A slow/failed query here (e.g. a statement timeout on a large
    // agent_heartbeats table) must NOT blank out the whole dashboard — degrade
    // gracefully to whatever we have instead of throwing.
    const agentHost = hostResult.error ? null : hostResult.data;
    const heartbeat = heartbeatResult.error ? null : heartbeatResult.data;
    return mapStatus(data, slotId, agentHost, heartbeat, slotResult.data);
  },
  async getSlotSettings(slotId: string): Promise<DashboardSettings | null> {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('slot_settings')
      .select('auto_bot_settings,collect_settings,training_settings,auto_hunt_settings')
      .eq('slot_id', slotId)
      .maybeSingle();
    if (error) {
      if (isMissingOptionalSettingsTable(error)) return null;
      throw error;
    }
    return data ? mapSlotSettings(data) : null;
  },
  async saveSlotSettings(slotId: string, settings: DashboardSettings): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase
      .from('slot_settings')
      .upsert(slotSettingsPayload(slotId, settings), { onConflict: 'slot_id' });
    if (error) {
      if (isMissingOptionalSettingsTable(error)) return;
      throw error;
    }
  },
  async getActiveLinkingSession(slotId: string): Promise<LinkingSession | null> {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('linking_sessions')
      .select('id,slot_id,slot_key,status,expires_at,assistance_requested_at,operator_active')
      .eq('slot_id', slotId)
      .eq('status', 'active')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      if (isMissingOptionalSettingsTable(error)) return null;
      throw error;
    }
    return data ? mapLinkingSession(data) : null;
  },
  async startLinkingSession(slotId: string): Promise<LinkingSession> {
    const supabase = getClient();
    const { data, error } = await supabase.rpc('start_account_linking', { target_slot_id: slotId });
    if (error) throw error;
    return mapLinkingSession(data);
  },
  async sendLinkInput(
    sessionId: string,
    eventType: LinkingEventType,
    payload: Record<string, unknown>
  ): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('queue_link_input', {
      target_session_id: sessionId,
      target_event_type: eventType,
      target_payload: payload
    });
    if (error) throw error;
  },
  async completeLinkingSession(sessionId: string): Promise<BotSlot> {
    const supabase = getClient();
    const { data, error } = await supabase.rpc('complete_account_linking', { target_session_id: sessionId });
    if (error) throw error;
    return mapSlot(data);
  },
  async cancelLinkingSession(sessionId: string, reason = 'user_cancelled'): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('cancel_account_linking', {
      target_session_id: sessionId,
      cancel_reason: reason
    });
    if (!error) return;
    if (reason !== 'user_cancelled' && isRpcSignatureMismatch(error)) {
      const fallback = await supabase.rpc('cancel_account_linking', { target_session_id: sessionId });
      if (fallback.error) throw fallback.error;
      return;
    }
    throw error;
  },
  async requestLinkingAssistance(sessionId: string): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('request_linking_assistance', { target_session_id: sessionId });
    if (error) throw error;
  },
  async sendLinkingChatMessage(sessionId: string, body: string): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('send_linking_chat_message', {
      target_session_id: sessionId,
      target_body: body
    });
    if (error) throw error;
  },
  async getLinkingChatMessages(sessionId: string): Promise<LinkingChatMessage[]> {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('linking_chat_messages')
      .select('id,session_id,sender_role,body,created_at')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .limit(200);
    if (error) {
      if (isMissingOptionalSettingsTable(error)) return [];
      throw error;
    }
    return (data || []).map(mapLinkingChatMessage);
  },
  subscribeLinkingChat(sessionId: string, onMessage: (message: LinkingChatMessage) => void): () => void {
    const supabase = getClient();
    const channel = supabase
      .channel(`linking_chat_${sessionId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'linking_chat_messages', filter: `session_id=eq.${sessionId}` },
        (payload) => onMessage(mapLinkingChatMessage(payload.new))
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  },
  async setLinkingOperatorActive(sessionId: string, active: boolean): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('set_linking_operator_active', {
      target_session_id: sessionId,
      active
    });
    if (error) throw error;
  },
  async listLinkingAssistanceRequests(): Promise<LinkingSession[]> {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('linking_sessions')
      .select('id,slot_id,slot_key,status,expires_at,assistance_requested_at,operator_active')
      .eq('status', 'active')
      .not('assistance_requested_at', 'is', null)
      .gt('expires_at', new Date().toISOString());
    if (error) {
      if (isMissingOptionalSettingsTable(error)) return [];
      throw error;
    }
    return (data || []).map(mapLinkingSession);
  },
  async saveReconnectSettings(slotId: string, settings: ReconnectSettings): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase
      .from('slot_settings')
      .upsert(reconnectSettingsPayload(slotId, settings), { onConflict: 'slot_id' });
    if (error) {
      if (isMissingOptionalSettingsTable(error)) return;
      throw error;
    }
  },
  async reconnectNow(slotId: string): Promise<BotCommand> {
    if (!config.allowCommands) {
      throw new Error('Comandos remotos desactivados en modo Supabase seguro.');
    }
    const supabase = getClient();
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) throw userError || new Error('Not authenticated');

    let slotResult: any = await supabase
      .from('bot_slots')
      .select(`id,slot_key,agent_id,${BOT_SLOT_DESIRED_STATE_SELECT}`)
      .eq('id', slotId)
      .single();
    if (slotResult.error && isMissingDesiredStateColumn(slotResult.error)) {
      slotResult = await supabase
        .from('bot_slots')
        .select('id,slot_key,agent_id')
        .eq('id', slotId)
        .single();
    }
    const { data: slot, error: slotError } = slotResult;
    if (slotError) throw slotError;
    const desiredWorkerState = String(slot.desired_worker_state || 'running');
    const desiredBotState = String(slot.desired_bot_state || 'idle');
    if (desiredWorkerState !== 'stopped' && desiredBotState === 'stopped') {
      try {
        await updateSlotDesiredStateIfAvailable(supabase, slotId, {
          desiredWorkerState: 'running',
          desiredBotState: 'idle',
          desiredReason: 'user_action:reconnect'
        });
      } catch (error) {
        console.warn('Could not update desired reconnect state before queuing reconnect command', error);
      }
    }

    const commandId = `web_${Date.now()}_${crypto.randomUUID()}`;
    const { data, error } = await supabase
      .from('commands')
      .insert({
        command_id: commandId,
        user_id: userData.user.id,
        slot_id: slotId,
        slot_key: slot.slot_key,
        agent_id: slot.agent_id || 'local-dev',
        action: 'reconnect',
        payload: {}
      })
      .select('command_id,action,status,queued_at,completed_at,error')
      .single();
    if (error) throw error;
    return mapCommand(data);
  },
  async listIntelligenceRequests(): Promise<IntelligenceRequest[]> {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('intelligence_requests')
      .select(
        'id,region_id,player_name,query,status,error,created_at,updated_at,requested_at,completed_at,user_hidden_at,intelligence_request_results(map_targets(player_name,alliance_tag,alliance_name,power,castle_x,castle_y,last_seen_at,confidence))'
      )
      .order('created_at', { ascending: false })
      .limit(25);
    if (error) throw error;
    return (data || []).map(mapIntelligenceRequest);
  },
  async submitIntelligenceRequest(playerName: string, regionId?: string): Promise<string> {
    const supabase = getClient();
    const trimmedRegionId = regionId?.trim();
    const { data, error } = trimmedRegionId
      ? await supabase.rpc('submit_intelligence_request', { player_name: playerName, search_region_id: trimmedRegionId })
      : await supabase.rpc('submit_intelligence_request', { player_name: playerName });
    if (error) throw error;
    return String(data);
  },
  async deleteIntelligenceRequest(requestId: string): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('hide_intelligence_request', { target_request_id: requestId });
    if (error) throw error;
  },
  async seedRegion(regionId: string, templateRegionId?: string): Promise<RegionSeedResult> {
    const supabase = getClient();
    const trimmed = regionId.trim();
    if (!trimmed) throw new Error('region_id requerido');
    const params: Record<string, string> = { target_region_id: trimmed };
    const trimmedTemplate = templateRegionId?.trim();
    if (trimmedTemplate) params.template_region_id = trimmedTemplate;
    const { data, error } = await supabase.rpc('admin_seed_region', params);
    if (error) throw error;
    const row = (data ?? {}) as Record<string, unknown>;
    return {
      regionId: String(row.region_id ?? trimmed),
      templateRegionId: row.template_region_id ? String(row.template_region_id) : null,
      inserted: Number(row.inserted ?? 0),
      totalCells: Number(row.total_cells ?? 0)
    };
  },
  async listRegionSummaries(): Promise<RegionSummary[]> {
    const supabase = getClient();
    const { data, error } = await supabase.rpc('admin_list_region_summaries');
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
      regionId: String(row.region_id),
      cellCount: Number(row.cell_count ?? 0),
      scannedCount: Number(row.scanned_count ?? 0),
      lastScanAt: (row.last_scan_at as string | null) ?? null
    }));
  },
  async listCommands(slotId: string): Promise<BotCommand[]> {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('commands')
      .select('command_id,action,status,queued_at,completed_at,error')
      .eq('slot_id', slotId)
      .order('queued_at', { ascending: false })
      .limit(10);
    if (error) throw error;
    return (data || []).map(mapCommand);
  },
  async sendCommand(request: CommandRequest): Promise<BotCommand> {
    if (!config.allowCommands) {
      throw new Error('Comandos remotos desactivados en modo Supabase seguro.');
    }
    const supabase = getClient();
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) throw userError || new Error('Not authenticated');
    const currentUser = await this.getCurrentUser();

    const { data: slot, error: slotError } = await supabase
      .from('bot_slots')
      .select(`id,slot_key,agent_id,user_id,${BOT_SLOT_OWNER_PROFILE_SELECT}`)
      .eq('id', request.slotId)
      .single();
    if (slotError) throw slotError;
    if (request.action === 'stop') {
      try {
        await updateSlotDesiredStateIfAvailable(supabase, request.slotId, {
          desiredWorkerState: 'running',
          desiredBotState: 'stopped',
          desiredReason: 'user_stop'
        });
      } catch (error) {
        console.warn('Could not update desired stop state before queuing stop command', error);
      }
    } else {
      await updateSlotDesiredStateIfAvailable(supabase, request.slotId, {
        desiredWorkerState: 'running',
        desiredBotState: desiredBotStateForManualCommand(request.action),
        desiredReason: `user_action:${request.action}`
      });
    }
    const supportPayload =
      currentUser.role === 'admin' && slot.user_id !== userData.user.id
        ? {
            ...request.payload,
            support: {
              actor_user_id: userData.user.id,
              actor_email: currentUser.email,
              actor_role: currentUser.role,
              owner_user_id: slot.user_id,
              owner_email: relatedProfileEmail(slot.profiles)
            }
          }
        : request.payload;
    const commandId = `web_${Date.now()}_${crypto.randomUUID()}`;
    const { data, error } = await supabase
      .from('commands')
      .insert({
        command_id: commandId,
        user_id: userData.user.id,
        slot_id: request.slotId,
        slot_key: slot.slot_key,
        agent_id: slot.agent_id || 'local-dev',
        action: request.action,
        payload: supportPayload
      })
      .select('command_id,action,status,queued_at,completed_at,error')
      .maybeSingle();
    if (error) throw error;
    if (data) return mapCommand(data);
    if (request.action === 'stop') return fallbackQueuedCommand(commandId, request.action);
    throw new Error('Command was queued but Supabase did not return a command row.');
  },
  async getAdminOverview(): Promise<AdminOverview> {
    const supabase = getClient();
    const [usersResult, slotRows, hostsResult] = await Promise.all([
      supabase.from('profiles').select('id,email,role').order('created_at', { ascending: false }),
      fetchAdminSlotRows(supabase),
      supabase
        .from('agent_hosts')
        .select('agent_id,label,status,last_seen_at,inventory,inventory_updated_at')
        .order('agent_id', { ascending: true })
    ]);
    if (usersResult.error) throw usersResult.error;
    if (hostsResult.error) throw hostsResult.error;
    return {
      users: (usersResult.data || []).map((row: any) => ({ id: row.id, email: row.email, role: row.role })),
      slots: slotRows.map((row: any) => ({
        ...mapSlot(row),
        userEmail: (usersResult.data || []).find((user: any) => user.id === row.user_id)?.email
      })),
      agentHosts: (hostsResult.data || []).map(mapAgentHost)
    };
  },
  async getAuditEvents(options = {}): Promise<AuditEventsPage> {
    const supabase = getClient();
    const limit = Math.max(1, Math.min(500, options.limit ?? 300));
    const offset = Math.max(0, options.offset ?? 0);
    let query = supabase
      .from('audit_events')
      .select('id,created_at,event_type,user_id,slot_id,agent_id,payload')
      .order('created_at', { ascending: false });
    if (options.slotId) {
      query = query.eq('slot_id', options.slotId);
    }
    if (options.agentId) {
      query = query.eq('agent_id', options.agentId);
    }
    const { data, error } = await query.range(offset, offset + limit);
    if (error) throw error;
    const rows = data || [];
    const hasMore = rows.length > limit;
    const events = rows.slice(0, limit).map(mapAuditEvent);
    return {
      events,
      limit,
      offset,
      total: null,
      hasMore
    };
  },
  async assignNextAvailableSlot(agentId?: string): Promise<BotSlot> {
    const supabase = getClient();
    const { data, error } = await supabase.rpc('admin_assign_next_available_slot', {
      desired_license_status: 'trial',
      ...(agentId ? { target_agent_id: agentId } : {})
    });
    if (error) throw error;
    return mapSlot(data);
  },
  async saveSlot(request: SaveSlotRequest): Promise<BotSlot> {
    const supabase = getClient();
    // With multi-server support, the same slot_key may exist on different agents.
    // We scope the existing-slot lookup to (slot_key, agent_id) to avoid cross-server conflicts.
    const [currentUserSlot, targetSlot] = await Promise.all([
      request.userId
        ? supabase.from('bot_slots').select('id,slot_key,user_id,agent_id').eq('user_id', request.userId).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      supabase
        .from('bot_slots')
        .select('id,slot_key,user_id,agent_id')
        .eq('slot_key', request.slotKey)
        .eq('agent_id', request.agentId)
        .maybeSingle()
    ]);
    if (currentUserSlot.error) throw currentUserSlot.error;
    if (targetSlot.error) throw targetSlot.error;
    if (targetSlot.data && request.userId && targetSlot.data.user_id !== request.userId) {
      throw new Error(`El slot ${request.slotKey} ya esta asignado a otro usuario`);
    }

    await supabase
      .from('agent_hosts')
      .upsert({ agent_id: request.agentId, label: request.agentId, status: 'offline' }, { onConflict: 'agent_id' });

    const requestedLicensePlan = request.licensePlan || licensePlanFromStatus(request.licenseStatus);
    const requestedLicenseStatus = toLicenseTableStatus(licenseStatusFromPlan(requestedLicensePlan));
    const requestedPlanKey = planKeyFromLicensePlan(requestedLicensePlan);
    let licenseId: string | null = null;
    let effectiveSlotLicenseStatus = licenseStatusFromPlan(requestedLicensePlan);

    if (request.userId) {
      const currentLicense = await supabase
        .from('licenses')
        .select('id,status,trial_ends_at,plan_key,provider')
        .eq('user_id', request.userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (currentLicense.error) throw currentLicense.error;

      const currentTrialEndsAt = currentLicense.data?.trial_ends_at || null;
      const nextTrialEndsAt =
        requestedLicenseStatus === 'trial'
          ? !currentTrialEndsAt || isPastIso(currentTrialEndsAt) || currentLicense.data?.status !== 'trial'
            ? fiveDaysFromNowIso()
            : currentTrialEndsAt
          : null;
      const effectiveLicenseStatus = requestedLicenseStatus;
      effectiveSlotLicenseStatus = licenseStatusFromPlan(requestedLicensePlan);

      const existingProvider = currentLicense.data?.provider;
      const keepExternalProvider = existingProvider && existingProvider !== 'manual';
      const licenseResult = currentLicense.data?.id
        ? await supabase
            .from('licenses')
            .update({
              ...(keepExternalProvider ? {} : { provider: 'manual' }),
              status: effectiveLicenseStatus,
              plan_key: requestedPlanKey,
              slots_allowed: 1,
              trial_ends_at: nextTrialEndsAt
            })
            .eq('id', currentLicense.data.id)
            .select('id')
            .single()
        : await supabase
            .from('licenses')
            .insert({
              user_id: request.userId,
              provider: 'manual',
              status: effectiveLicenseStatus,
              plan_key: requestedPlanKey,
              slots_allowed: 1,
              trial_ends_at: nextTrialEndsAt
            })
            .select('id')
            .single();
      if (licenseResult.error) throw licenseResult.error;
      licenseId = licenseResult.data.id;
    }

    const slotPayload = {
      slot_key: request.slotKey,
      user_id: request.userId || null,
      agent_id: request.agentId,
      label: request.label,
      ...(request.regionId?.trim()
        ? {
          region_id: request.regionId.trim(),
          region_status: 'verified',
          region_source: 'admin',
          region_verified_at: new Date().toISOString()
        }
        : {}),
      ...(licenseId ? { license_id: licenseId } : {}),
      license_status: effectiveSlotLicenseStatus,
      linked: request.linked,
      state: request.linked ? 'linked' : 'slot_reserved',
      enabled_modules: request.enabledModules
    };
    const existingSlot = currentUserSlot.data || targetSlot.data;
    const slotResult = existingSlot
      ? await supabase
          .from('bot_slots')
          .update(slotPayload)
          .eq('id', existingSlot.id)
          .select(`${SLOT_SELECT},licenses(trial_ends_at,status,plan_key,provider)`)
          .single()
      : await supabase
          .from('bot_slots')
          .insert(slotPayload)
          .select(`${SLOT_SELECT},licenses(trial_ends_at,status,plan_key,provider)`)
          .single();
    const { data, error } = slotResult;
    if (error) throw error;

    const statusResult = await supabase.from('slot_status').upsert(
      {
        slot_id: data.id,
        agent_id: request.agentId,
        state: request.linked ? 'linked' : 'slot_reserved',
        running: false,
        last_result: null,
        next_check_seconds: null,
        auto_hunt_progress: null,
        training_queue_status: null,
        world_march_status: null,
        screenshot_path: null,
        latest_log_line: request.linked
          ? 'Slot linked. Waiting for local agent status.'
          : 'Slot reserved. Waiting for account linking and local BlueStacks validation.'
      },
      { onConflict: 'slot_id' }
    );
    if (statusResult.error) throw statusResult.error;

    const cleanStatusResult = await supabase
      .from('slot_status')
      .update({
        last_result: null,
        next_check_seconds: null,
        auto_hunt_progress: null,
        training_queue_status: null,
        world_march_status: null,
        screenshot_path: null
      })
      .eq('slot_id', data.id);
    if (cleanStatusResult.error) throw cleanStatusResult.error;

    return mapSlot(data);
  },
  async updateSlotDesiredState(slotId: string, update: SlotDesiredStateUpdate): Promise<BotSlot> {
    const supabase = getClient();
    const slot = await updateSlotDesiredStateIfAvailable(supabase, slotId, update);
    if (!slot) {
      throw new Error('Supabase aun no tiene las columnas desired_*; aplica la migracion 044_deployment_desired_state.sql');
    }
    return slot;
  },
  async releaseSlot(slotId: string): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('admin_release_slot', { target_slot_id: slotId });
    if (error) throw error;
  },
  async sendAdminSlotCommand(action, payload) {
    const supabase = getClient();
    const agentId = String(payload.agent_id || '');
    if (!agentId) throw new Error('agent_id requerido para comandos admin remotos');
    const commandId = `admin_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const { error } = await supabase.from('commands').insert({
      command_id: commandId,
      agent_id: agentId,
      slot_key: 'agent',
      action,
      payload
    });
    if (error) throw error;
    return { commandId };
  },
  async getAdminCommandStatus(commandId: string): Promise<AdminCommandSnapshot | null> {
    const supabase = getClient();
    const { data, error } = await supabase
      .from('commands')
      .select('command_id,action,status,queued_at,delivered_at,completed_at,error,result')
      .eq('command_id', commandId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapAdminCommandSnapshot(data) : null;
  },
  async listWaitlist(): Promise<WaitlistEntry[]> {
    const supabase = getClient();
    const { data, error } = await supabase.rpc('admin_list_waitlist');
    if (error) {
      if (isRpcSignatureMismatch(error)) return [];
      throw error;
    }
    return ((data as Record<string, unknown>[]) || []).map(mapWaitlistEntry);
  },
  async promoteWaitlistEntry(id: string): Promise<void> {
    const supabase = getClient();
    const { error } = await supabase.rpc('admin_promote_waitlist', { p_id: id });
    if (error) throw error;
  }
};

function mapWaitlistEntry(row: Record<string, unknown>): WaitlistEntry {
  return {
    id: String(row.id),
    email: String(row.email ?? ''),
    status: (row.status as WaitlistEntry['status']) ?? 'waiting',
    locale: (row.locale as string | null) ?? null,
    source: String(row.source ?? 'landing'),
    createdAt: String(row.created_at ?? ''),
    invitedAt: (row.invited_at as string | null) ?? null,
    inviteExpiresAt: (row.invite_expires_at as string | null) ?? null,
    inviteEmailSentAt: (row.invite_email_sent_at as string | null) ?? null,
    claimedAt: (row.claimed_at as string | null) ?? null
  };
}
